import React from 'react';
import PropTypes from 'prop-types';
import CheckIcon from '@mui/icons-material/Check';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';

import { useTheme } from 'contexts/ThemeContext';

import moment from 'utils/moment';

import { Table, Text } from 'components/DataDisplay';
import { Button } from 'components/Inputs';
import { Column, Container, Item, Row } from 'components/Layout';

const euroFormat = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR'
});

const formatAmount = amount => euroFormat.format(amount);

export const formatEuros = amount => `${formatAmount(amount)} TTC`;

const formatPeriod = row => {
  const period =
    row.start && row.end
      ? `${moment(row.start).format('DD/MM')} → ${moment(row.end).format(
          'DD/MM'
        )}`
      : '-';

  return period;
};

const creativesCountOf = (campaigns, key) => {
  const campaign = campaigns.find(item => item.key === key);
  const creativesCount = campaign?.creativesCount ?? '-';

  return creativesCount;
};

const BillingQuote = ({
  quote,
  campaigns,
  customerName,
  awaitingValidation,
  billed,
  debitedAmount,
  busy,
  onValidate,
  onCancel
}) => {
  const { themeColors } = useTheme();
  const shownAmount = formatEuros(billed ? debitedAmount : quote.total);
  const billedLabel = billed ? 'facturées' : 'à facturer';

  const renderStatus = row => (
    <Text
      component="span"
      fontWeight={400}
      color={row.funded ? 'activation' : 'error'}
    >
      {row.funded ? 'Terminé' : 'Brouillon'}
    </Text>
  );

  const renderPrice = row => {
    if (!row.funded) {
      return '-';
    }

    return (
      <Column style={{ textAlign: 'right' }}>
        <Text fontSize="12.5px" fontWeight={400}>
          {formatAmount(row.amount)}
        </Text>
        {row.unitAmount !== null && row.campaignsCount > 1 && (
          <Text fontSize="11px" style={{ color: themeColors.themeV2.subtitle }}>
            {row.campaignsCount} × {formatAmount(row.unitAmount)}
          </Text>
        )}
      </Column>
    );
  };

  const renderReason = row => {
    if (row.funded || !row.reason) {
      return '-';
    }

    const amountText =
      row.amount === null ? '' : ` (${formatAmount(row.amount)})`;
    const reasonText = `${row.reason}${amountText}`;

    return reasonText;
  };

  return (
    <Container
      style={{
        border: `1px solid ${themeColors.designSystem.line}`,
        borderRadius: '10px',
        marginTop: '16px',
        marginBottom: '16px',
        overflow: 'hidden'
      }}
    >
      <Container style={{ padding: '20px' }}>
        <Row
          spacing={0}
          justify="flex-start"
          alignItems="center"
          style={{ gap: '8px' }}
        >
          <Item flex>
            <ReceiptLongOutlinedIcon style={{ color: themeColors.pml }} />
          </Item>
          <Item flex>
            <Text fontWeight={700} fontSize="15px">
              Récapitulatif de l&apos;import
            </Text>
          </Item>
          {billed && (
            <Item flex style={{ marginLeft: 'auto' }}>
              <Text
                component="span"
                fontSize="12px"
                fontWeight={700}
                style={{
                  padding: '4px 14px',
                  borderRadius: '999px',
                  color: themeColors.designSystem.greenDark,
                  backgroundColor: themeColors.designSystem.greenBg
                }}
              >
                Facturé
              </Text>
            </Item>
          )}
        </Row>
        <Row
          spacing={0}
          justify="flex-start"
          alignItems="baseline"
          style={{ columnGap: '32px', rowGap: '6px', marginTop: '16px' }}
        >
          <Item flex>
            <Text fontSize="13px">
              <b>Campagnes {billedLabel} :</b> {quote.campaignsCount}{' '}
              <span style={{ color: themeColors.themeV2.subtitle }}>
                (1 par magasin · {quote.sourcesCount} campagnes source)
              </span>
            </Text>
          </Item>
          <Item flex>
            <Text fontSize="13px">
              <b>Compte de facturation :</b> {customerName}
            </Text>
          </Item>
          <Item flex>
            <Text fontSize="13px">
              <b>{billed ? 'Montant débité :' : 'Montant total à débiter :'}</b>{' '}
              <Text
                component="span"
                fontSize="15px"
                fontWeight={700}
                color="pml"
              >
                {shownAmount}
              </Text>
            </Text>
          </Item>
        </Row>
        {awaitingValidation && (
          <Row
            spacing={0}
            justify="flex-start"
            alignItems="center"
            style={{ gap: '6px', marginTop: '10px' }}
          >
            <Item flex>
              <InfoOutlinedIcon
                style={{ fontSize: '15px', color: themeColors.pml }}
              />
            </Item>
            <Item flex>
              <Text
                fontSize="12px"
                style={{ color: themeColors.themeV2.subtitle }}
              >
                Vérifiez les campagnes et le montant avant de valider. Aucun
                montant n&apos;a encore été débité.
              </Text>
            </Item>
          </Row>
        )}
        <Row spacing={0} style={{ marginTop: '16px' }}>
          <Item
            style={{
              border: `1px solid ${themeColors.designSystem.line}`,
              borderRadius: '8px',
              overflow: 'hidden'
            }}
          >
            <Table
              noBoxShadow
              noBorderRadius
              noBorderColor
              headers={[
                { id: 'campaignKey', label: 'Campagne source' },
                { id: 'shops', label: 'Magasins repiqués' },
                { id: 'creatives', label: 'Créas repiquées' },
                { id: 'period', label: 'Période' },
                { id: 'status', label: 'Status' },
                { id: 'reason', label: 'Motif' },
                { id: 'price', label: 'Prix', headerJustify: 'flex-end' }
              ]}
              rows={quote.rows.map((row, index) => ({
                id: `${row.key}-${index}`,
                campaignKey: row.name || row.key,
                shops: row.campaignsCount,
                creatives: creativesCountOf(campaigns, row.key),
                period: formatPeriod(row),
                status: renderStatus(row),
                reason: renderReason(row),
                price: renderPrice(row)
              }))}
              footer={
                <Row
                  spacing={0}
                  justify="space-between"
                  alignItems="center"
                  style={{
                    padding: '12px 16px',
                    borderBottom: `1px solid ${themeColors.designSystem.line}`,
                    backgroundColor: themeColors.designSystem.bgSoft
                  }}
                >
                  <Item flex>
                    <Text fontSize="12.5px" fontWeight={700}>
                      Total · {quote.sourcesCount} campagnes source ·{' '}
                      {quote.shopsCount} magasins = {quote.campaignsCount}{' '}
                      campagnes {billedLabel}
                    </Text>
                  </Item>
                  <Item flex>
                    <Text fontSize="12.5px" fontWeight={700}>
                      {shownAmount}
                    </Text>
                  </Item>
                </Row>
              }
            />
          </Item>
        </Row>
      </Container>
      {awaitingValidation && (
        <Row
          spacing={0}
          justify="flex-end"
          style={{
            gap: '12px',
            padding: '16px 20px',
            borderTop: `1px solid ${themeColors.designSystem.line}`
          }}
        >
          <Item flex>
            <Button
              onClick={onCancel}
              variant="outlined"
              color="error"
              disabled={busy}
              upperCase
            >
              <Text fontSize="inherit" fontWeight="inherit" color="inherit">
                Annuler l&apos;import
              </Text>
            </Button>
          </Item>
          <Item flex>
            <Button
              onClick={onValidate}
              variant="contained"
              color={themeColors.pml}
              startIcon={<CheckIcon />}
              disabled={busy}
              upperCase
            >
              <Text fontSize="inherit" fontWeight="inherit" color="inherit">
                Valider et facturer
              </Text>
            </Button>
          </Item>
        </Row>
      )}
    </Container>
  );
};

BillingQuote.propTypes = {
  quote: PropTypes.shape({
    total: PropTypes.number,
    campaignsCount: PropTypes.number,
    sourcesCount: PropTypes.number,
    shopsCount: PropTypes.number,
    rows: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any))
  }).isRequired,
  campaigns: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)),
  customerName: PropTypes.string.isRequired,
  awaitingValidation: PropTypes.bool,
  billed: PropTypes.bool,
  debitedAmount: PropTypes.number,
  busy: PropTypes.bool,
  onValidate: PropTypes.func,
  onCancel: PropTypes.func
};

BillingQuote.defaultProps = {
  campaigns: [],
  awaitingValidation: false,
  billed: false,
  debitedAmount: 0,
  busy: false,
  onValidate: () => {},
  onCancel: () => {}
};

export default BillingQuote;
